import { SvgBuildingColumns } from '@cocoonspace/icons/react/font-awesome/light/building-columns'
import { SvgPen } from '@cocoonspace/icons/react/font-awesome/light/pen'
import { useGetAccountPaymentMethods } from '@cocoonspace/sdk-js/domains/account/account'
import type { AccountPaymentMethod } from '@cocoonspace/sdk-js/types/accountPaymentMethod'
import { Plus } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import { type ComponentProps, useState } from 'react'
import { Button } from '~/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '~/components/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '~/components/ui/select'
import { Separator } from '~/components/ui/separator'
import { PaymentMethodCardIcon } from '~/domains/payments/components/payment-method-card-icon'
import { PaymentMethodsManager } from '~/domains/payments/components/templates/payment-methods-manager'

const CardItem = ({ pm }: { pm: AccountPaymentMethod }) => {
	const { t } = useTranslation()

	return (
		<div className='flex flex-row items-center gap-4'>
			<PaymentMethodCardIcon
				brand={pm.card?.brand}
				className='size-6'
			/>

			<div className='flex flex-col items-start'>
				<strong>{pm.title}</strong>

				<div className='text-sm'>{pm.name}</div>

				<div className='flex flex-row items-center gap-2 text-xs'>
					{`**** **** **** ${pm.card?.last_4}`}

					<div className='ml-auto'>
						{t('myAccountPaymentMethods:expires_on', {
							month: pm.card?.exp_month?.toString().padStart(2, '0'),
							year: pm.card?.exp_year,
						})}
					</div>
				</div>
			</div>
		</div>
	)
}

const UpdateDialog = (props: ComponentProps<typeof Dialog>) => {
	const { t } = useTranslation()

	return (
		<Dialog {...props}>
			<DialogContent className='w-full max-w-6xl overflow-auto bg-slate-100 max-md:z-[100] max-md:h-screen'>
				<DialogHeader className='text-left'>
					<DialogTitle>{t('myAccountPaymentMethods:title')}</DialogTitle>
				</DialogHeader>

				<PaymentMethodsManager />
			</DialogContent>
		</Dialog>
	)
}

const UpdateBtn = ({ children, ...props }: ComponentProps<typeof Button>) => (
	<Button
		type='button'
		variant='link'
		size='sm'
		className='mx-auto flex p-2'
		{...props}
	>
		{children}
	</Button>
)

export const PaymentMethodSelector = ({
	disabled,
	value,
	onChange,
}: {
	disabled?: boolean
	value?: string
	onChange: (value: string) => void
}) => {
	const { t } = useTranslation()
	const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)

	const { data: pms } = useGetAccountPaymentMethods({
		query: {
			select: (res) => res.data.filter((pm) => pm.type === 'card') || [],
		},
	})

	const defaultCard = pms?.find((pm) => pm.id === value)

	if (!defaultCard) {
		return (
			<div className='flex flex-col items-center justify-center rounded border p-4 pb-2'>
				<div>{t('myAccountPaymentMethods:noPaymentMethod')}</div>

				<UpdateBtn onClick={() => setIsUpdateDialogOpen(true)}>
					<Plus
						size={16}
						className='mr-1'
					/>

					{t('actions:add.btnLabel')}
				</UpdateBtn>

				<UpdateDialog
					open={isUpdateDialogOpen}
					onOpenChange={setIsUpdateDialogOpen}
				/>
			</div>
		)
	}

	if (defaultCard?.type === 'sepa_credit') {
		return (
			<div className='flex flex-row items-center gap-4 rounded border p-4'>
				<SvgBuildingColumns />

				<div className='flex flex-col'>
					<strong className='leading-none'>
						{t('myAccountPaymentMethods:sepa.long')}
					</strong>

					<div className='text-sm'>
						{t('myAccountPaymentMethods:sepa.short')}
					</div>
				</div>
			</div>
		)
	}

	return (
		<Select
			disabled={disabled}
			value={value}
			onValueChange={onChange}
		>
			<SelectTrigger className='h-[80px]'>
				<SelectValue />
			</SelectTrigger>

			<SelectContent>
				{pms?.map((pm) => (
					<SelectItem
						key={pm.id}
						value={pm.id!}
					>
						<CardItem pm={pm} />
					</SelectItem>
				))}

				<Separator />

				<UpdateBtn onClick={() => setIsUpdateDialogOpen(true)}>
					<SvgPen className='mr-1 text-sm' />

					{t('actions:update.btnLabel')}
				</UpdateBtn>
			</SelectContent>

			<UpdateDialog
				open={isUpdateDialogOpen}
				onOpenChange={setIsUpdateDialogOpen}
			/>
		</Select>
	)
}
